import type { TaskApproval } from "@rivets/core";
import { apiErrorText } from "@/features/workers/worker-access";
import { useNow } from "@/hooks/useNow";
import type { TaskApprovalDecision } from "@/lib/api";
import {
  approvalAge,
  approvalAlreadyDecided,
  approvalDecisionText,
  approvalRequestedLine,
  approvalSourceUrl,
  useDecideTaskApproval,
  useTaskApproval,
} from "./taskApproval";

/**
 * The approval in front of a chat whose first turn came from outside Rivets
 * — a Linear issue, a Slack message. The task view and the assistant drawer
 * both mount this one component.
 *
 * Pending, it is the card: who asked and through what, the full prompt the
 * agent will be told, and Approve / Reject. Approved, it renders nothing: the
 * transcript running beneath it says so, and a line pinned above every turn
 * for good was noise. Rejected, it keeps one line, because nothing else on the
 * page says why the task stopped. Mirrors `TaskApprovalSection` on native.
 *
 * `enabled` is whether to look the approval up at all; a 404 (the chat never
 * needed one) renders nothing.
 */
export function TaskApprovalPanel({ chatId, enabled, awaiting }: {
  chatId: string;
  enabled: boolean;
  /** The chat itself says it is waiting, so a card is due even before the
   * approval has loaded. */
  awaiting: boolean;
}) {
  const query = useTaskApproval(chatId, enabled);
  const approval = query.data;
  const decide = useDecideTaskApproval(approval);

  if (approval?.status === "approved") return null;
  if (approval && approval.status !== "pending") return <ApprovalBanner approval={approval} />;
  if (approval) {
    return (
      <ApprovalCardView
        approval={approval}
        deciding={decide.isPending ? decide.variables ?? null : null}
        error={decide.isError
          ? approvalAlreadyDecided(decide.error)
            ? "Someone already decided this. Refreshing…"
            : apiErrorText(decide.error, "Could not record the decision. Try again.")
          : null}
        onDecide={(decision) => decide.mutate(decision)}
      />
    );
  }
  if (!awaiting) return null;
  return (
    <section className="approval-card" aria-label="Approval needed">
      <div className="approval-card-head"><strong className="approval-card-title">Approval needed</strong></div>
      {query.isError ? (
        <div className="approval-card-error" role="alert">
          {apiErrorText(query.error, "Could not load this approval.")}{" "}
          <button type="button" className="btn btn-compact btn-ghost" onClick={() => void query.refetch()}>Retry</button>
        </div>
      ) : (
        <p className="approval-card-note">{query.isPending ? "Loading the request…" : "This task is waiting for a member to approve it."}</p>
      )}
    </section>
  );
}

export function ApprovalCardView({ approval, deciding, error, onDecide, now }: {
  approval: TaskApproval;
  /** The decision in flight, if any: both buttons wait for it. */
  deciding: TaskApprovalDecision | null;
  error: string | null;
  onDecide: (decision: TaskApprovalDecision) => void;
  now?: number;
}) {
  const sourceUrl = approvalSourceUrl(approval.source);
  const busy = deciding !== null;
  const approveBlocked = !approval.canApprove;
  return (
    <section className="approval-card" aria-label="Approval needed">
      <div className="approval-card-head">
        <strong className="approval-card-title">Approval needed</strong>
        <span className="approval-card-meta">
          {approvalRequestedLine(approval)}
          <span aria-hidden="true"> · </span>
          <ApprovalAge timestamp={approval.requestedAt} now={now} />
        </span>
      </div>
      <div className="approval-card-source">
        {sourceUrl
          ? <a href={sourceUrl} target="_blank" rel="noopener noreferrer" className="approval-card-source-link">{approval.source.label}</a>
          : <span>{approval.source.label}</span>}
      </div>
      <p className="approval-card-note">This prompt includes text from outside Rivets. Approving runs it on a worker as you.</p>
      {/* A text node in a <pre>: the prompt is external text and is never
          parsed as Markdown or HTML. Focusable so it scrolls from the keyboard. */}
      <pre className="approval-card-prompt" tabIndex={0} aria-label="Prompt">{approval.prompt}</pre>
      <div className="approval-card-actions">
        {approveBlocked && approval.cannotApproveReason
          ? <span className="approval-card-reason">{approval.cannotApproveReason}</span>
          : null}
        {approval.canReject ? (
          <button
            type="button"
            className="btn btn-compact btn-ghost"
            disabled={busy}
            aria-busy={deciding === "reject"}
            onClick={() => onDecide("reject")}
          >{deciding === "reject" ? "Rejecting…" : "Reject"}</button>
        ) : null}
        <button
          type="button"
          className="btn btn-compact btn-primary"
          disabled={busy || approveBlocked}
          aria-busy={deciding === "approve"}
          title={approveBlocked ? approval.cannotApproveReason ?? "You cannot approve this" : "Run this prompt on a worker as you"}
          onClick={() => onDecide("approve")}
        >{deciding === "approve" ? "Approving…" : "Approve"}</button>
      </div>
      {error ? <div className="approval-card-error" role="alert">{error}</div> : null}
    </section>
  );
}

/** "5m ago", re-read once a minute in this leaf alone; `now` pins it for tests. */
function ApprovalAge({ timestamp, now }: { timestamp: number; now?: number }) {
  const ticking = useNow(60_000, now === undefined);
  return (
    <time dateTime={new Date(timestamp).toISOString()} title={new Date(timestamp).toLocaleString()}>
      {approvalAge(timestamp, now ?? ticking)}
    </time>
  );
}

/** What a decided approval leaves behind: one quiet line. */
export function ApprovalBanner({ approval }: { approval: TaskApproval }) {
  const text = approvalDecisionText(approval);
  if (!text) return null;
  const sourceUrl = approvalSourceUrl(approval.source);
  return (
    <div className={`approval-banner is-${approval.status}`} role="status">
      <span className="approval-banner-dot" aria-hidden="true" />
      <span className="approval-banner-text truncate" title={text}>{text}</span>
      {sourceUrl ? <a className="approval-banner-link" href={sourceUrl} target="_blank" rel="noopener noreferrer">{approval.source.label}</a> : null}
    </div>
  );
}
